'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { MoveDiagonal2 } from 'lucide-react'
import { useZoom } from '@/contexts/zoom-context'

/**
 * StickMe — general-purpose "pin to the margin" wrapper. Once the wrapped
 * element's top edge reaches the viewport top, it docks to the paper's right
 * edge and follows the reader down the page. Works for any content: videos,
 * database schemas, diagrams, images, overviews.
 *
 * The element stays in `<main>` the whole time (never reparented — so an
 * embedded iframe is never reloaded). Pinning is done purely with `transform`,
 * which doesn't affect layout, so the original slot stays reserved and shows a
 * labelled grey placeholder. Living inside the zoomed `<main>`, zoom is handled
 * by dividing the translate by the live zoom factor (the scale uses on-screen
 * widths so zoom cancels there). See docs/internals/POSITIONING.
 *
 * Pinned size = a fraction of the paper width (right edge anchored to the paper
 * edge, height follows the aspect ratio). A round handle at the bottom-left
 * resizes it; the chosen width fraction is persisted in localStorage per
 * `storageKey` (local only, not synced). `defaultWidth` ("800px", "800",
 * "50%") sets the width used until the reader drags the handle; px are CSS px
 * of the unzoomed paper, so they scale with zoom like the paper does.
 *
 * `footer` is an optional node shown beneath the pinned element (e.g. the
 * coupled-video toggle). `enabled` false is a transparent passthrough.
 */

const DEFAULT_WIDTH_FRACTION = 1 / 3 // pinned width = this × the paper width
const MIN_WIDTH_FRACTION = 0.15
const MAX_WIDTH_FRACTION = 1
const TOP_MARGIN = 8
const FOOTER_GAP = 8
const DEFAULT_STORAGE_KEY = 'stickme:width-fraction'

function clampFraction(f: number): number {
  return Math.min(MAX_WIDTH_FRACTION, Math.max(MIN_WIDTH_FRACTION, f))
}

/** "50%" → fraction 0.5; "800px" / "800" → 800 px; anything else → null. */
function parseDefaultWidth(w?: string): { fraction: number } | { px: number } | null {
  const m = w?.trim().match(/^(\d+(?:\.\d+)?)\s*(%|px)?$/i)
  if (!m) return null
  const n = parseFloat(m[1])
  if (!(n > 0)) return null
  return m[2] === '%' ? { fraction: n / 100 } : { px: n }
}

// Page-wide count of currently-pinned StickMe instances. While > 0 we put
// `stickme-pinned` on <body> so the floating top-right page controls (font
// size / theme / login) fade out and yield the corner to the docked content.
// The pinned element can't simply be lifted above those controls with z-index
// — it lives inside the zoom-transformed, opaque <main>, which sits below them
// — and portalling it out would reload pinned video iframes. So we hide the
// controls while something is docked; they return when nothing is pinned.
let pinnedInstanceCount = 0
function adjustPinnedCount(delta: number) {
  if (typeof document === 'undefined') return
  pinnedInstanceCount = Math.max(0, pinnedInstanceCount + delta)
  document.body.classList.toggle('stickme-pinned', pinnedInstanceCount > 0)
}

/**
 * Coordinates multiple StickMe instances on a page so only ONE is pinned at a
 * time: the lowest-on-page instance whose top has scrolled above the viewport.
 * Scrolling down hands the pinned slot from one to the next; scrolling back up
 * hands it back. Optional — without a provider each StickMe pins independently.
 */
interface StickMeRegistry {
  register: (id: string, getTop: () => number) => void
  unregister: (id: string) => void
  /** The instance that should be pinned given the current viewport top, or null. */
  activeId: (vpTop: number) => string | null
}
const StickMeContext = createContext<StickMeRegistry | null>(null)
export function useStickMeRegistry(): StickMeRegistry | null {
  return useContext(StickMeContext)
}

export function StickMeProvider({ children }: { children: ReactNode }) {
  const reg = useRef(new Map<string, () => number>())
  const register = useCallback((id: string, getTop: () => number) => {
    reg.current.set(id, getTop)
  }, [])
  const unregister = useCallback((id: string) => {
    reg.current.delete(id)
  }, [])
  const activeId = useCallback((vpTop: number) => {
    let id: string | null = null
    let best = -Infinity
    for (const [key, getTop] of reg.current) {
      const top = getTop()
      // Eligible = scrolled to/above the viewport top. The active one is the
      // lowest on the page among those (the greatest — least-negative — top).
      if (top <= vpTop + TOP_MARGIN && top > best) {
        best = top
        id = key
      }
    }
    return id
  }, [])
  const value = useMemo(() => ({ register, unregister, activeId }), [register, unregister, activeId])
  return <StickMeContext.Provider value={value}>{children}</StickMeContext.Provider>
}

export function StickMe({
  children,
  footer,
  enabled = true,
  storageKey = DEFAULT_STORAGE_KEY,
  defaultWidth,
  className,
  style,
}: {
  children: ReactNode
  footer?: ReactNode
  enabled?: boolean
  storageKey?: string
  // Pinned width before the reader resizes: "800px", "800" or "50%" (of the
  // paper width). Invalid/absent → DEFAULT_WIDTH_FRACTION.
  defaultWidth?: string
  // Author-supplied class/style forwarded to the content wrapper. Lets authors
  // add a background, padding, border, etc. via plain CSS — handy for
  // transparent assets (e.g. Excalidraw SVGs) that would otherwise show the
  // page text through them once pinned. Applied both inline and while pinned;
  // note that `transform`/`position`/`zIndex` are managed by the pin logic and
  // will be overridden while docked.
  className?: string
  style?: CSSProperties
}) {
  const slotRef = useRef<HTMLSpanElement>(null)
  const innerRef = useRef<HTMLSpanElement>(null)
  const footerRef = useRef<HTMLSpanElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)
  const getZoom = useZoom()
  const registry = useStickMeRegistry()
  const myId = useId()
  const [pinned, setPinned] = useState(false)
  const pinnedRef = useRef(false)
  // Reader-chosen width (drag / localStorage); null = use defaultWidth.
  const widthFractionRef = useRef<number | null>(null)
  const defaultWidthRef = useRef(parseDefaultWidth(defaultWidth))
  const scheduleRef = useRef<() => void>(() => {})
  const draggingRef = useRef(false)
  useEffect(() => {
    defaultWidthRef.current = parseDefaultWidth(defaultWidth)
    scheduleRef.current()
  }, [defaultWidth])

  // Restore the persisted width on mount (local only, per storageKey).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const v = parseFloat(window.localStorage.getItem(storageKey) ?? '')
    widthFractionRef.current = Number.isNaN(v) ? null : clampFraction(v)
  }, [storageKey])

  // Join the page-level registry so only one StickMe is pinned at a time.
  useEffect(() => {
    if (!registry || !enabled) return
    registry.register(myId, () => slotRef.current?.getBoundingClientRect().top ?? Infinity)
    return () => registry.unregister(myId)
  }, [registry, enabled, myId])

  // While this instance is docked, count it toward the page-wide pinned total
  // (fades the floating top-right controls — see adjustPinnedCount). Cleanup
  // decrements on unpin and on unmount-while-pinned.
  useEffect(() => {
    if (!pinned) return
    adjustPinnedCount(1)
    return () => adjustPinnedCount(-1)
  }, [pinned])

  useEffect(() => {
    if (!enabled) return
    // The page editor preview scrolls in its own pane: pin inside that pane
    // (its edges act as viewport and paper). Elsewhere: #scroll-container.
    const previewPane = slotRef.current?.closest<HTMLElement>('#markdown-preview-scroll-container') ?? null
    const sc = previewPane ?? document.getElementById('scroll-container')
    let raf: number | null = null

    const clear = () => {
      for (const el of [innerRef.current, footerRef.current, handleRef.current]) {
        if (!el) continue
        el.style.transform = ''
        el.style.transformOrigin = ''
      }
      if (innerRef.current) {
        innerRef.current.style.position = ''
        innerRef.current.style.zIndex = ''
      }
      if (footerRef.current) {
        footerRef.current.style.position = ''
        footerRef.current.style.zIndex = ''
      }
      if (handleRef.current) {
        handleRef.current.style.left = ''
        handleRef.current.style.top = ''
      }
    }

    const update = () => {
      raf = null
      const slot = slotRef.current
      const inner = innerRef.current
      if (!slot || !inner) return

      // Slot is never transformed → reports the element's natural on-screen rect.
      const a = slot.getBoundingClientRect()
      const scRect = sc?.getBoundingClientRect()
      // Annotation mode: #scroll-container is the fixed-height scroller, its top
      // is the visible top. Reflow mode: the window scrolls and the container's
      // top goes negative — clamp to the viewport top, else nothing ever pins.
      const vpTop = Math.max(0, scRect?.top ?? 0)

      // Pin once the element's top edge reaches the viewport's top edge. With
      // a registry, only the lowest-on-page eligible instance pins (so a later
      // StickMe takes over the slot from an earlier one).
      const shouldPin =
        a.height > 0 &&
        (registry ? registry.activeId(vpTop) === myId : a.top <= vpTop + TOP_MARGIN)
      if (shouldPin !== pinnedRef.current) {
        pinnedRef.current = shouldPin
        setPinned(shouldPin)
      }
      if (!shouldPin) {
        if (inner.style.transform) clear()
        return
      }

      const z = getZoom() || 1
      const paper = previewPane ?? document.getElementById('paper')
      const pRect = paper && paper.offsetWidth > 0 ? paper.getBoundingClientRect() : scRect
      const paperWidth = pRect?.width ?? window.innerWidth

      // Width fraction: reader's drag wins, else the author default. A px
      // default is converted against the paper's layout (unzoomed) width.
      let fraction = widthFractionRef.current
      if (fraction === null) {
        const d = defaultWidthRef.current
        const layoutWidth = paper && paper.offsetWidth > 0 ? paper.offsetWidth : paperWidth / z
        fraction =
          d && 'px' in d ? clampFraction(d.px / layoutWidth)
          : d ? clampFraction(d.fraction)
          : DEFAULT_WIDTH_FRACTION
      }
      const targetWidth = paperWidth * fraction // on-screen px
      const scale = targetWidth / a.width
      // Always anchor the right edge to the viewport (scroll-container) right
      // edge, not #paper's — under zoom the paper's right edge drifts off-screen.
      // Width is still a fraction of the paper width; only the anchor is the
      // viewport. The resize handler below uses this same anchor.
      const anchorRight = (scRect?.right ?? window.innerWidth) - TOP_MARGIN
      const targetLeft = anchorRight - targetWidth
      const targetTop = vpTop + TOP_MARGIN // top edge at the viewport top

      // Local translate is pre-ancestor-scale, so divide on-screen deltas by z.
      inner.style.transformOrigin = 'top left'
      inner.style.transform = `translate(${(targetLeft - a.left) / z}px, ${(targetTop - a.top) / z}px) scale(${scale})`
      inner.style.position = 'relative'
      inner.style.zIndex = '50'

      // Anchor to the actual media element's rendered rect — wrappers around
      // an inline iframe/img leave a baseline gap below it, so the inner span
      // can be a touch taller than what's visible. Fall back to the wrapper.
      // Find the content media to anchor to. NB: no 'svg' — the handle's own
      // icon and decorative icons (e.g. a callout's) are SVGs and would be
      // matched instead. Excalidraw/schemas render as <img>, so they're
      // covered; arbitrary content with no media falls back to the wrapper.
      // Skip display:none matches (zero rect): Excalidraw renders a light and a
      // dark <img>, one always hidden, so querySelector's first match would be
      // the hidden one in the inactive theme and fling the handle to the origin.
      const media =
        Array.from(
          inner.querySelectorAll('iframe, video, img, canvas'),
        ).find((el) => el.getBoundingClientRect().width > 0) ?? null
      const ir = (media ?? inner).getBoundingClientRect()

      // Handle lives *inside* inner, so it inherits inner's transform exactly.
      // Position it at the media's bottom-left in inner-local coordinates
      // (screen offset ÷ inner's total on-screen scale), centre it on that
      // point, and counter-scale (1/scale) so it stays a fixed size.
      if (handleRef.current) {
        const innerRect = inner.getBoundingClientRect()
        const total = inner.offsetWidth > 0 ? innerRect.width / inner.offsetWidth : scale * z
        const localX = (ir.left - innerRect.left) / total
        const localY = (ir.bottom - innerRect.top) / total
        const hs = handleRef.current.style
        hs.left = `${localX}px`
        hs.top = `${localY}px`
        hs.transformOrigin = 'top left'
        hs.transform = `scale(${1 / scale}) translate(-50%, -50%)`
      }

      const ftr = footerRef.current
      if (ftr) {
        const naturalCenterX = a.left + a.width / 2
        const tx = (ir.left + ir.width / 2 - naturalCenterX) / z
        const ty = (ir.bottom + FOOTER_GAP - a.bottom) / z
        ftr.style.transformOrigin = 'top left'
        ftr.style.transform = `translate(${tx}px, ${ty}px)`
        ftr.style.position = 'relative'
        ftr.style.zIndex = '50'
      }
    }

    const schedule = () => {
      if (raf === null) raf = requestAnimationFrame(update)
    }
    scheduleRef.current = schedule

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('wheel', schedule, { passive: true })
    window.addEventListener('touchmove', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    sc?.addEventListener('scroll', schedule, { passive: true })

    // The wrapped media can settle a few px *after* the first layout (e.g. a
    // YouTube thumbnail swapping to a higher-res image, or an iframe loading) —
    // recompute when its size changes so the handle/footer don't end up offset.
    const ro = new ResizeObserver(() => schedule())
    if (innerRef.current) ro.observe(innerRef.current)
    if (slotRef.current) ro.observe(slotRef.current)

    schedule()
    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('wheel', schedule)
      window.removeEventListener('touchmove', schedule)
      window.removeEventListener('resize', schedule)
      sc?.removeEventListener('scroll', schedule)
      ro.disconnect()
      if (raf !== null) cancelAnimationFrame(raf)
      clear()
    }
  }, [enabled, getZoom, registry, myId])

  // The handle only mounts once `pinned` flips true, which is after the
  // update() that set it — re-run layout so the handle gets counter-scaled.
  useEffect(() => {
    if (pinned) scheduleRef.current()
  }, [pinned])

  // --- Resize handle drag (width fraction; right edge stays anchored) ---
  const onHandlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    draggingRef.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onHandlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const previewPane = slotRef.current?.closest<HTMLElement>('#markdown-preview-scroll-container') ?? null
    const paper = previewPane ?? document.getElementById('paper')
    const pRect = paper?.getBoundingClientRect()
    if (!pRect || pRect.width <= 0) return
    // Match the pin anchor: right edge fixed at the viewport (scroll-container)
    // right edge, width grows leftward toward the pointer. Both terms are
    // on-screen px so the fraction is zoom-independent. Measuring from #paper's
    // right edge (off-screen when zoomed) made the width jump away from the cursor.
    const sc = previewPane ?? document.getElementById('scroll-container')
    const anchorRight = (sc?.getBoundingClientRect().right ?? window.innerWidth) - TOP_MARGIN
    const onScreenWidth = anchorRight - e.clientX
    widthFractionRef.current = clampFraction(onScreenWidth / pRect.width)
    scheduleRef.current()
  }
  const onHandlePointerUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* pointer already released */
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, String(widthFractionRef.current))
    }
  }

  if (!enabled) {
    return (
      <>
        {className || style ? (
          <span className={className ? `block ${className}` : 'block'} style={style}>
            {children}
          </span>
        ) : (
          children
        )}
        {footer}
      </>
    )
  }

  return (
    <>
      <span ref={slotRef} className="relative block">
        <span ref={innerRef} className="block">
          {/* Author class/style wrap only the content, not the handle/overlay,
              and sit *inside* innerRef so the pin transform (set imperatively on
              innerRef) is never clobbered by React re-applying `style`. Omitted
              entirely when unstyled to keep the original DOM byte-identical. */}
          {className || style ? (
            <span className={className ? `block ${className}` : 'block'} style={style}>
              {children}
            </span>
          ) : (
            children
          )}
          {/* Handle lives inside the player so it inherits the pin transform;
              its left/top are set imperatively to the media's bottom-left. */}
          {pinned && (
            <button
              ref={handleRef}
              type="button"
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              title="Drag to resize"
              aria-label="Resize pinned element"
              className="absolute z-60 flex h-7 w-7 items-center justify-center rounded-full border border-background/60 bg-foreground/85 text-background shadow-md"
              style={{ left: 0, top: 0, cursor: 'nesw-resize', touchAction: 'none' }}
            >
              <MoveDiagonal2 className="h-3.5 w-3.5 rotate-90" />
            </button>
          )}
        </span>
        {pinned && (
          <span
            className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/40 text-xs text-muted-foreground"
            onClick={() => slotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          >
            Pinned to the margin — click to jump back
          </span>
        )}
      </span>
      {footer != null && (
        <span ref={footerRef} className="block">
          {footer}
        </span>
      )}
    </>
  )
}
